// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { RedliningProvider } from './contexts/RedliningContext'; // Import RedliningProvider
import { OnboardingProvider } from './contexts/OnboardingContext'; // Import OnboardingProvider
import analyticsService from './services/analytics';
import AuthAwareRoot from './components/AuthAwareRoot';
import LoginPage from './components/LoginPage';
import CredentialLoginPage from './components/CredentialLoginPage';
import SignUpPage from './pages/SignUpPage'; // Signup disabled
import Dashboard from './pages/Dashboard';
import DocumentPage from './pages/DocumentPage';
import DocumentPreviewPage from './pages/DocumentPreviewPage';
import SignatureView from './pages/SignatureView';
import PlanComparison from './pages/static/PlanComparison';
import PrivacyPolicy from './pages/static/PrivacyPolicy';
import TermsOfService from './pages/static/TermsOfService';
import VisionPage from './pages/static/VisionPage';
import CompliancePage from './pages/static/CompliancePage';
import SignatureSentPage from './pages/SignatureSentPage';
import DocsGate from './pages/DocsGate';
import ErrorBoundary from './components/ErrorBoundary';
import PaymentProtectedRoute from './components/PaymentProtectedRoute';
// Import new redlining pages
import RedliningPage from './pages/RedliningPage';
import RedliningViewer from './pages/RedliningViewer';
import NewDocument from './pages/NewDocument';
import ContactsPage from './pages/ContactsPage';
import SettingsPage from './pages/SettingsPage';
import PreviewDebugger from './components/PreviewDebugger';
// Import the new WorkflowReplacementDemo page
import WorkflowReplacementDemo from './pages/static/WorkflowReplacementDemo';
// Import the new ROI Calculator page
import ROICalculator from './pages/static/ROICalculator';
// Import the Problem Details page
import ProblemDetailsPage from './pages/ProblemDetailsPage';
// Import the Waitlist page
import WaitlistPage from './pages/WaitlistPage';
// Import the Generation page
import GenerationPage from './pages/GenerationPage';
import TrainingPage from './pages/TrainingPage';
import TrainingEditorPage from './pages/TrainingEditorPage';
import TextRegionExtractorPage from './pages/TextRegionExtractorPage';
// Import the Join Workspace page
import JoinWorkspacePage from './pages/JoinWorkspacePage';
// Import email verification and password reset pages
import EmailVerificationPage from './pages/EmailVerificationPage';
import PasswordResetPage from './pages/PasswordResetPage';
import EmailVerificationRequired from './components/EmailVerificationRequired';
// Import global QueryClient instance
import { queryClient } from './utils/queryClient';

// Prefer env-based client id so we can use a dev OAuth client that allows localhost
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '92668879435-qftp70jcjucam3jkgd0b1er3fv43n28t.apps.googleusercontent.com';

// Protected route component
const ProtectedRoute = ({ children, allowSignatureTokens = false, requireEmailVerification = true }) => {
  const { user, loading, needsEmailVerification } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // If signature tokens are allowed, check for token in URL
  if (allowSignatureTokens) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasSignatureToken = urlParams.has('token');
    
    // Allow access if user is logged in OR has a signature token
    if (user || hasSignatureToken) {
      // Still check email verification for logged-in users without signature tokens
      if (user && !hasSignatureToken && requireEmailVerification && needsEmailVerification()) {
        return <EmailVerificationRequired />;
      }
      return children;
    }
  } else {
    // Regular protected route - requires login
    if (user) {
      // Check email verification requirement
      if (requireEmailVerification && needsEmailVerification()) {
        return <EmailVerificationRequired />;
      }
      return children;
    }
  }
  
  return <Navigate to="/" replace />;
};

function App() {
  // Initialize analytics on app load - always enable data collection
  React.useEffect(() => {
    analyticsService.initialize(true);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <OnboardingProvider>
            <PermissionsProvider>
              <RedliningProvider> {/* Add RedliningProvider */}
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<AuthAwareRoot />} />
                  
                  {/* Revolutionary Workflow Replacement Demo */}
                  <Route path="/workflow-demo" element={<WorkflowReplacementDemo />} />
                  
                  {/* ROI Calculator */}
                  <Route path="/roi-calculator" element={<ROICalculator />} />
                  
                  {/* Problem Details */}
                  <Route path="/problem-details" element={<ProblemDetailsPage />} />
                  
                  {/* Waitlist */}
                  <Route path="/waitlist" element={<WaitlistPage />} />
                  
                  {/* Join Workspace */}
                  <Route path="/join-workspace" element={<JoinWorkspacePage />} />
                  
                  {/* Authentication routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/login/credentials" element={<CredentialLoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  
                  {/* Email verification and password reset */}
                  <Route path="/verify-email" element={<EmailVerificationPage />} />
                  <Route path="/reset-password" element={<PasswordResetPage />} />
                  
                  <Route path="/pricing" element={<WorkflowReplacementDemo />} />
                  <Route path="/plan-comparison" element={<PlanComparison />} />
                  
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  
                  <Route path="/terms" element={<TermsOfService />} />
                  
                  <Route path="/vision" element={<VisionPage />} />
                  
                  <Route path="/compliance" element={<CompliancePage />} />
                  
                  {/* Gate docs behind auth-like experience: if not logged in show gate, else redirect to docs host */}
                  <Route path="/docs" element={<DocsGate />} />
                  <Route path="/docs/*" element={<DocsGate />} />
                  
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <Dashboard />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/document/:id" 
                    element={
                      <ProtectedRoute allowSignatureTokens={true}>
                        <ErrorBoundary>
                          <DocumentPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />

                  <Route 
                    path="/document/:id/text-location" 
                    element={
                      <ProtectedRoute allowSignatureTokens={true}>
                        <ErrorBoundary>
                          <DocumentPage mode="text-location" />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />

                  <Route 
                    path="/document/:id/text-location/inverted" 
                    element={
                      <ProtectedRoute allowSignatureTokens={true}>
                        <ErrorBoundary>
                          <TextRegionExtractorPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />

                  {/* Post-signature confirmation page – no auth required (token param optional) */}
                  <Route
                    path="/signature-sent/:id"
                    element={<SignatureSentPage />}
                  />
                  
                  <Route 
                    path="/preview/:id" 
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <DocumentPreviewPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />
                <Route path="/new-document" element={
                  <PaymentProtectedRoute>
                    <NewDocument />
                  </PaymentProtectedRoute>
                } />

                <Route path="/contacts" element={
                  <ProtectedRoute>
                    <ContactsPage />
                  </ProtectedRoute>
                } />

                <Route path="/settings" element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } />

                <Route path="/debug-preview" element={
                  <ProtectedRoute>
                    <PreviewDebugger />
                  </ProtectedRoute>
                } />

                <Route path="/generation" element={
                  <PaymentProtectedRoute>
                    <ErrorBoundary>
                      <GenerationPage />
                    </ErrorBoundary>
                  </PaymentProtectedRoute>
                } />

                {/* Training */}
                <Route path="/training" element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <TrainingPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/training/:documentId/edit" element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <TrainingEditorPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />

                  
                  {/* Public document view - doesn't require authentication */}
                  <Route 
                    path="/view/:id" 
                    element={
                      <ErrorBoundary>
                        <DocumentPage publicMode={true} />
                      </ErrorBoundary>
                    } 
                  />
                  
                  <Route 
                    path="/signatures/:signatureId" 
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <SignatureView />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Add new redlining routes */}
                  <Route 
                    path="/redlining" 
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <RedliningPage />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route 
                    path="/redlining/:documentId" 
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <RedliningViewer />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Add other routes as needed */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
              </RedliningProvider>
            </PermissionsProvider>
          </OnboardingProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
      
      {/* Toast notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#374151',
            color: '#fff',
          },
        }}
      />
      
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;